// import { useState } from "react";
// import ImageUpload from "../components/imageupload";

// export default function ProductImages() {
//   const [category, setCategory] = useState("");
//   const [files, setFiles] = useState<File[]>([]);

//   const handleSubmit = (e: React.FormEvent) => {
//     e.preventDefault();
//     if (!category) return;
//     // TODO: send `{ category, files }` to backend
//     alert(
//       `Category: ${category}\n${files.length} image(s) ready to upload!`
//     );
//   };

//   return (
//     <section className="p-6">
//       <h1 className="text-2xl font-semibold mb-4">Product Upload</h1>

//       <form
//         onSubmit={handleSubmit}
//         className="space-y-6 bg-white p-6 rounded-xl shadow"
//       >
//         {/* Category field */}
//         <div>
//           <label className="block mb-1 font-medium" htmlFor="category">
//             Category
//           </label>
//           <input
//             id="category"
//             type="text"
//             value={category}
//             onChange={(e) => setCategory(e.target.value)}
//             placeholder="e.g. Electronics, Apparel"
//             className="w-full border rounded-lg p-2 outline-none focus:ring-2 focus:ring-indigo-500"
//             required
//           />
//         </div>

//         {/* Images */}
//         <ImageUpload
//           label="Product Images"
//           multiple
//           onFilesSelected={setFiles}
//         />

//         <button
//           type="submit"
//           disabled={!category || files.length === 0}
//           className="px-4 py-2 rounded-lg bg-indigo-600 text-white disabled:opacity-50"
//         >
//           Upload
//         </button>
//       </form>
//     </section>
//   );
// }

import { useEffect, useState } from "react";
import ImageUpload from "../components/imageupload";

interface CategoryRecord {
  id: number;
  category_name: string;
  created_at: string;
  image_urls: string[];
}

interface GroupedCategory {
  category_name: string;
  created_at: string;
  image_urls: string[];
}

const API_URL =
  "https://adinsinternational.com/websitebackend/category_api.php";

export default function ProductImages() {
  const [category, setCategory] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<CategoryRecord[]>([]);
  const [groupedCategories, setGroupedCategories] = useState<GroupedCategory[]>(
    []
  );

  const fetchCategories = async () => {
    try {
      const res = await fetch(API_URL);
      const json = await res.json();
      if (json.status === "success") {
        // Group categories by category_name
        const groupedMap = new Map<string, GroupedCategory>();

        json.data.forEach((item: CategoryRecord) => {
          const existing = groupedMap.get(item.category_name);
          if (existing) {
            // Append images to existing category
            existing.image_urls.push(...item.image_urls);
          } else {
            // Create new grouped entry
            groupedMap.set(item.category_name, {
              category_name: item.category_name,
              created_at: item.created_at,
              image_urls: [...item.image_urls],
            });
          }
        });

        setGroupedCategories(Array.from(groupedMap.values()));
      } else {
        throw new Error(json.message || "Unknown error");
      }
    } catch (err) {
      console.error(err);
      setError("Could not load categories");
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!category || files.length === 0) return;
    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append("category_name", category);
      files.forEach((f) => formData.append("images[]", f));

      const res = await fetch(API_URL, { method: "POST", body: formData });
      const json = await res.json();

      if (json.status === "success") {
        setCategory("");
        setFiles([]);
        await fetchCategories();
        alert("Uploaded successfully ✅");
      } else {
        throw new Error(json.message || "Upload failed");
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this category and its images?")) return;

    try {
      const body = new URLSearchParams({ id: id.toString() });
      const res = await fetch(API_URL, {
        method: "DELETE",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const json = await res.json();
      if (json.status === "success") {
        setCategories((prev) => prev.filter((c) => c.id !== id));
      } else {
        throw new Error(json.message);
      }
    } catch (err) {
      console.error(err);
      alert("Delete failed ❌");
    }
  };

  const ImageCarousel = ({ images }: { images: string[] }) => {
    const [index, setIndex] = useState(0);

    if (images.length === 0) return null;

    const prev = () => setIndex((i) => (i === 0 ? images.length - 1 : i - 1));
    const next = () => setIndex((i) => (i === images.length - 1 ? 0 : i + 1));
    return (
      <div className="relative">
        <img
          src={images[index]}
          alt={`Image ${index + 1}`}
          className="rounded-lg border border-gray-200 w-full object-contain max-h-64"
          loading="lazy"
        />
        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute top-1/2 left-2 -translate-y-1/2 bg-gray-700 text-white rounded-full px-2 py-1"
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              onClick={next}
              className="absolute top-1/2 right-2 -translate-y-1/2 bg-gray-700 text-white rounded-full px-2 py-1"
              aria-label="Next"
            >
              ›
            </button>
          </>
        )}
      </div>
    );
  };

  return (
    <section className="p-6 space-y-10">
      <form
        onSubmit={handleSubmit}
        className="space-y-6 bg-white p-6 rounded-xl shadow"
      >
        <h1 className="text-2xl font-semibold">Add Product Category</h1>

        <div>
          <label className="block mb-1 font-medium" htmlFor="category">
            Category name
          </label>
          <input
            id="category"
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full border rounded-lg p-2 outline-none focus:ring-2 focus:ring-indigo-500"
            required
          />
        </div>

        <ImageUpload
          label="Product images"
          multiple
          onFilesSelected={setFiles}
        />

        {error && <p className="text-sm text-red-600 font-medium">{error}</p>}

        <button
          type="submit"
          disabled={loading || !category || files.length === 0}
          className="px-4 py-2 rounded-lg bg-indigo-600 text-white disabled:opacity-50"
        >
          {loading ? "Uploading…" : "Upload"}
        </button>
      </form>
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Existing Categories</h2>

        {groupedCategories.length === 0 ? (
          <p className="text-gray-600">No categories uploaded yet.</p>
        ) : (
          <ul className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {groupedCategories.map((category, idx) => (
              <li
                key={category.category_name + idx}
                className="bg-white rounded-xl shadow p-4 flex flex-col"
              >
                <div className="flex-1">
                  <h3 className="font-bold mb-2">{category.category_name}</h3>
                  <p className="text-xs text-gray-500 mb-4">
                    {category.created_at !== "0000-00-00 00:00:00"
                      ? new Date(category.created_at).toLocaleString()
                      : "N/A"}
                  </p>

                  <ImageCarousel images={category.image_urls} />
                </div>

                <button
                  className="mt-4 w-full text-sm text-white bg-red-600 rounded-lg py-2 hover:bg-red-700"
                  // Adjust delete to delete by category name if needed
                  onClick={() =>
                    alert(
                      "Delete functionality needs update to handle grouped items"
                    )
                  }
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

// src/pages/ProductImages.tsx
// import { useEffect, useState } from "react";

// import ImageUpload from "../components/imageupload";

// /* ----------  types  ---------- */
// interface CategoryRecord {
//   id: number;
//   category_name: string;
//   created_at: string;
//   image_urls: string[];
// }

// /* ----------  constants  ---------- */
// // If you prefer env vars, rename to REACT_APP_API_URL and add to .env.local
// const API_URL =
//   "https://adinsinternational.com/websitebackend/category_api.php";

// /* ----------  component  ---------- */
// export default function ProductImages() {
//   const [category, setCategory]   = useState("");
//   const [files, setFiles]         = useState<File[]>([]);
//   const [loading, setLoading]     = useState(false);
//   const [error, setError]         = useState<string | null>(null);
//   const [categories, setCategories] = useState<CategoryRecord[]>([]);

//   /* -------- fetch all existing records -------- */
//   const fetchCategories = async () => {
//     try {
//       const res  = await fetch(API_URL);
//       const json = await res.json();
//       if (json.status === "success") setCategories(json.data);
//       else throw new Error(json.message || "Unknown error");
//     } catch (err) {
//       console.error(err);
//       setError("Could not load categories");
//     }
//   };

//   useEffect(() => {
//     fetchCategories();
//   }, []);

//   /* --------------- submit new item --------------- */
//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     if (!category || files.length === 0) return;
//     setLoading(true);
//     setError(null);

//     try {
//       const formData = new FormData();
//       formData.append("category_name", category);
//       files.forEach((f) => formData.append("images[]", f));

//       const res  = await fetch(API_URL, { method: "POST", body: formData });
//       const json = await res.json();

//       if (json.status === "success") {
//         // clear form & refresh list
//         setCategory("");
//         setFiles([]);
//         await fetchCategories();
//         alert("Uploaded successfully ✅");
//       } else {
//         throw new Error(json.message || "Upload failed");
//       }
//     } catch (err: any) {
//       console.error(err);
//       setError(err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* --------------- delete item --------------- */
//   const handleDelete = async (id: number) => {
//     if (!window.confirm("Delete this category and its images?")) return;

//     try {
//       const body = new URLSearchParams({ id: id.toString() });
//       const res  = await fetch(API_URL, {
//         method: "DELETE",
//         headers: { "Content-Type": "application/x-www-form-urlencoded" },
//         body:     body.toString(),
//       });
//       const json = await res.json();
//       if (json.status === "success") {
//         setCategories((prev) => prev.filter((c) => c.id !== id));
//       } else {
//         throw new Error(json.message);
//       }
//     } catch (err) {
//       console.error(err);
//       alert("Delete failed ❌");
//     }
//   };

//   /* --------------- UI --------------- */
//   return (
//     <section className="p-6 space-y-10">
//       {/* ---------- form ---------- */}
//       <form
//         onSubmit={handleSubmit}
//         className="space-y-6 bg-white p-6 rounded-xl shadow"
//       >
//         <h1 className="text-2xl font-semibold">Add Product Category</h1>

//         <div>
//           <label className="block mb-1 font-medium" htmlFor="category">
//             Category name
//           </label>
//           <input
//             id="category"
//             type="text"
//             value={category}
//             onChange={(e) => setCategory(e.target.value)}
//             className="w-full border rounded-lg p-2 outline-none focus:ring-2 focus:ring-indigo-500"
//             required
//           />
//         </div>

//         <ImageUpload
//           label="Product images"
//           multiple
//           onFilesSelected={setFiles}
//         />

//         {error && (
//           <p className="text-sm text-red-600 font-medium">{error}</p>
//         )}

//         <button
//           type="submit"
//           disabled={loading || !category || files.length === 0}
//           className="px-4 py-2 rounded-lg bg-indigo-600 text-white disabled:opacity-50"
//         >
//           {loading ? "Uploading…" : "Upload"}
//         </button>
//       </form>

//       {/* ---------- list ---------- */}
//       <div className="space-y-4">
//         <h2 className="text-xl font-semibold">Existing Categories</h2>

//         {categories.length === 0 ? (
//           <p className="text-gray-600">No categories uploaded yet.</p>
//         ) : (
//           <ul className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
//             {/* {categories.map((c) => (
//               <li
//                 key={c.id}
//                 className="bg-white rounded-xl shadow p-4 flex flex-col"
//               >
//                 <div className="flex-1">
//                   <h3 className="font-bold mb-2">{c.category_name}</h3>
//                   <p className="text-xs text-gray-500 mb-4">
//                     {new Date(c.created_at).toLocaleString()}
//                   </p>

//                   {c.image_urls[0] && (
//                     <img
//                       src={c.image_urls[0]}
//                       alt={c.category_name}
//                       className="w-full h-32 object-cover rounded-lg"
//                     />
//                   )}
//                 </div>

//                 <button
//                   onClick={() => handleDelete(c.id)}
//                   className="mt-4 px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
//                 >
//                   Delete
//                 </button>
//               </li>
//             ))} */}
//             {categories.map((c, index) => (
//   <li key={`${c.id}-${index}`} className="bg-white rounded-xl shadow p-4 flex flex-col">
//     <div className="flex-1">
//       <h3 className="font-bold mb-2">{c.category_name}</h3>
//       <p className="text-xs text-gray-500 mb-4">
//         {c.created_at !== "0000-00-00 00:00:00"
//           ? new Date(c.created_at).toLocaleString()
//           : "N/A"}
//       </p>

//       {c.image_urls[0] && (
//         <img
//           src={encodeURI(c.image_urls[0])}
//           alt={c.category_name}
//           className="w-full h-32 object-cover rounded-lg"
//         />
//       )}
//     </div>

//     <button
//       onClick={() => handleDelete(Number(c.id))}
//       className="mt-4 px-3 py-1.5 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700"
//     >
//       Delete
//     </button>
//   </li>
// ))}

//           </ul>
//         )}
//       </div>
//     </section>
//   );
// }
